import React from 'react';
import { Helmet } from 'react-helmet-async';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import GavelIcon from '@material-ui/icons/Gavel';
import AccountBalanceOutlinedIcon from '@material-ui/icons/AccountBalanceOutlined';
import TranslateIcon from '@material-ui/icons/Translate';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { tokens } from '../../theme';
import ChatBot from '../ChatBot/ChatBot';
import LiaMascot from './LiaMascot';
import { LIA } from './liaBrand';

const HELPS = [
    {
        icon: <DescriptionOutlinedIcon />,
        title: 'Find documents and reports',
        text: 'State Budget books, execution reports, laws and other Ministry publications, with links to the files.',
    },
    {
        icon: <GavelIcon />,
        title: 'Tenders and procurement',
        text: 'Open tenders, closing dates and how to take part through the e-Procurement Portal.',
    },
    {
        icon: <AccountBalanceOutlinedIcon />,
        title: 'Ministry services and contacts',
        text: 'What each directorate does, where to go for a service and how to contact the Ministry.',
    },
    {
        icon: <TranslateIcon />,
        title: 'In your language',
        text: 'Ask in Tetum, Português or English. LIA answers in the language you choose.',
    },
];

const RESPONSIBLE_USE = [
    'LIA answers from the Ministry’s published website content. Always check important details in the official document it links to.',
    'Never enter passwords, bank or card details, identity numbers or other personal information.',
    'Your conversation history is kept only in this browser, so you can return to it. Clear it from the chat history panel at any time.',
    'LIA is a guide, not an official decision. For formal requests, contact the Ministry directly.',
];

// Tais weave, repeated as a band (same motif as the mascot's sash).
function TaisBand({ className }) {
    return (
        <svg className={className} width="100%" height="18" aria-hidden="true" focusable="false" preserveAspectRatio="none">
            <defs>
                <pattern id="lia-page-tais" width="18" height="18" patternUnits="userSpaceOnUse">
                    <rect width="18" height="18" fill="#a62121" />
                    <path d="M9 2 L16 9 L9 16 L2 9 Z" fill="#c8a02a" />
                    <path d="M9 5.5 L12.5 9 L9 12.5 L5.5 9 Z" fill="#0e2748" />
                    <path d="M0 0 L4 0 L0 4 Z M18 0 L14 0 L18 4 Z M0 18 L4 18 L0 14 Z M18 18 L14 18 L18 14 Z" fill="#111827" />
                    <circle cx="9" cy="9" r="1.1" fill="#ffffff" />
                </pattern>
            </defs>
            <rect width="100%" height="18" fill="url(#lia-page-tais)" />
        </svg>
    );
}

const useStyles = makeStyles((theme) => ({
    hero: {
        position: 'relative',
        overflow: 'hidden',
        color: '#ffffff',
        backgroundColor: tokens.blue900,
        backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.14) 1px, transparent 0),
            linear-gradient(115deg, ${tokens.blue900} 0%, ${tokens.blue700} 60%, ${tokens.blue600} 100%)`,
        backgroundSize: '24px 24px, cover',
    },
    heroInner: {
        paddingTop: theme.spacing(9),
        paddingBottom: theme.spacing(9),
        [theme.breakpoints.down('sm')]: { paddingTop: theme.spacing(6), paddingBottom: theme.spacing(6) },
    },
    eyebrow: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 12,
        fontSize: '0.8125rem',
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: tokens.goldSoft,
        '&::before': { content: '""', width: 36, height: 3, backgroundColor: tokens.gold },
    },
    name: {
        margin: theme.spacing(2, 0, 0.5),
        // Explicit, so site-wide heading styles cannot recolour or centre it.
        color: '#ffffff',
        textAlign: 'left',
        fontFamily: tokens.headingFont,
        fontWeight: 800,
        fontSize: 'clamp(3rem, 7vw, 4.75rem)',
        lineHeight: 1,
        letterSpacing: '-0.03em',
    },
    nameAi: { color: tokens.gold },
    expansion: {
        margin: 0,
        fontSize: '1.125rem',
        fontWeight: 600,
        color: 'rgba(255,255,255,0.85)',
        letterSpacing: '0.02em',
    },
    tagline: {
        margin: theme.spacing(3, 0, 1),
        fontFamily: tokens.headingFont,
        fontSize: 'clamp(1.25rem, 2.4vw, 1.625rem)',
        fontWeight: 600,
        lineHeight: 1.35,
        maxWidth: 560,
    },
    taglineEnglish: { margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '1rem' },
    actions: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1.5), marginTop: theme.spacing(4) },
    primary: {
        backgroundColor: tokens.gold,
        color: tokens.blue900,
        fontWeight: 700,
        padding: theme.spacing(1.25, 3),
        borderRadius: 999,
        textTransform: 'none',
        fontSize: '1rem',
        '&:hover': { backgroundColor: tokens.goldSoft },
    },
    secondary: {
        color: '#ffffff',
        borderColor: 'rgba(255,255,255,0.6)',
        fontWeight: 600,
        padding: theme.spacing(1.25, 3),
        borderRadius: 999,
        textTransform: 'none',
        fontSize: '1rem',
        '&:hover': { borderColor: '#ffffff', backgroundColor: 'rgba(255,255,255,0.08)' },
    },
    mascotStage: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 300,
        '&::before': {
            content: '""',
            position: 'absolute',
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 70%)',
        },
    },
    mascot: {
        position: 'relative',
        filter: 'drop-shadow(0 18px 30px rgba(0, 0, 0, 0.35))',
        animation: '$float 5s ease-in-out infinite',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    },
    '@keyframes float': {
        '0%, 100%': { transform: 'translateY(0)' },
        '50%': { transform: 'translateY(-10px)' },
    },
    bubble: {
        position: 'absolute',
        top: 18,
        right: '6%',
        padding: '10px 16px',
        borderRadius: '16px 16px 16px 4px',
        backgroundColor: '#ffffff',
        color: tokens.blue900,
        fontWeight: 700,
        fontSize: '0.9375rem',
        boxShadow: tokens.shadowLg,
        [theme.breakpoints.down('xs')]: { right: 0 },
    },
    band: { display: 'block', borderTop: `3px solid ${tokens.gold}` },
    section: { paddingTop: theme.spacing(8), paddingBottom: theme.spacing(8) },
    muted: { backgroundColor: tokens.surface, borderTop: `1px solid ${tokens.border}`, borderBottom: `1px solid ${tokens.border}` },
    overline: {
        display: 'block',
        fontSize: '0.75rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: tokens.blue600,
        marginBottom: theme.spacing(1),
    },
    heading: {
        margin: theme.spacing(0, 0, 1),
        fontFamily: tokens.headingFont,
        fontWeight: 700,
        fontSize: 'clamp(1.625rem, 3vw, 2.125rem)',
        color: tokens.blue900,
    },
    lead: { margin: theme.spacing(0, 0, 4), color: tokens.muted, fontSize: '1.0625rem', maxWidth: 720 },
    helpCard: {
        height: '100%',
        padding: theme.spacing(3),
        borderRadius: tokens.radius + 4,
        backgroundColor: tokens.white,
        border: `1px solid ${tokens.border}`,
        borderTop: `4px solid ${tokens.gold}`,
        boxShadow: tokens.shadowSm,
    },
    helpIcon: {
        display: 'inline-flex',
        padding: 10,
        borderRadius: 12,
        backgroundColor: tokens.blue50,
        color: tokens.blue600,
        marginBottom: theme.spacing(1.5),
    },
    helpTitle: { margin: theme.spacing(0, 0, 1), fontSize: '1.0625rem', fontWeight: 700, color: tokens.blue900 },
    helpText: { margin: 0, color: tokens.inkSoft, lineHeight: 1.6, fontSize: '0.9375rem' },
    aboutCard: {
        height: '100%',
        padding: theme.spacing(4),
        borderRadius: tokens.radius + 4,
        backgroundColor: tokens.white,
        border: `1px solid ${tokens.border}`,
    },
    aboutTitle: { margin: theme.spacing(0, 0, 2), fontFamily: tokens.headingFont, fontSize: '1.375rem', color: tokens.blue900 },
    aboutText: { margin: theme.spacing(0, 0, 1.5), color: tokens.inkSoft, lineHeight: 1.7 },
    useList: { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: theme.spacing(1.75) },
    useItem: { display: 'flex', gap: theme.spacing(1.5), color: tokens.inkSoft, lineHeight: 1.6 },
    useIcon: { color: tokens.green, flexShrink: 0, marginTop: 2 },
}));

/**
 * LIA's own page: www.mof.gov.tl/lia, and the home page of lia.mof.gov.tl.
 * Introduces the assistant, then embeds the full chat.
 */
export default function LiaPage() {
    const classes = useStyles();

    const startChat = (event) => {
        const target = document.getElementById('lia-chat');
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Put the cursor in the message box once the chat is in view.
        window.setTimeout(() => document.querySelector('#lia-chat textarea')?.focus({ preventScroll: true }), 500);
    };

    return (
        <>
            <Helmet defer={false}>
                <title>{`${LIA.brand}: ${LIA.expansion} | Ministry of Finance, Timor-Leste`}</title>
                <meta name="description" content={`${LIA.tagline} ${LIA.taglineEnglish} Ask the Ministry of Finance's AI assistant in Tetum, Português or English.`} />
            </Helmet>

            <section className={classes.hero} aria-labelledby="lia-title">
                <Container maxWidth="lg">
                    <Grid container spacing={6} alignItems="center" className={classes.heroInner}>
                        <Grid item xs={12} md={7}>
                            <span className={classes.eyebrow}>Ministry of Finance · AI assistant</span>
                            <h1 id="lia-title" className={classes.name}>
                                LIA <span className={classes.nameAi}>AI</span>
                            </h1>
                            <p className={classes.expansion}>{LIA.expansion}</p>
                            <p className={classes.tagline} lang="tet">{LIA.tagline}</p>
                            <p className={classes.taglineEnglish}>{LIA.taglineEnglish}</p>
                            <div className={classes.actions}>
                                <Button href="#lia-chat" onClick={startChat} variant="contained" className={classes.primary} endIcon={<ArrowDownwardIcon />}>
                                    Start a conversation
                                </Button>
                                <Button href="/chatbot" target="_blank" rel="noopener noreferrer" variant="outlined" className={classes.secondary} endIcon={<OpenInNewIcon />}>
                                    Open full screen
                                </Button>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <div className={classes.mascotStage}>
                                <LiaMascot size={240} className={classes.mascot} title="LIA, the Ministry of Finance AI assistant mascot" />
                                <span className={classes.bubble} lang="tet" aria-hidden="true">Ola! Ha'u mak LIA.</span>
                            </div>
                        </Grid>
                    </Grid>
                </Container>
                <TaisBand className={classes.band} />
            </section>

            <section className={classes.section} aria-labelledby="lia-helps">
                <Container maxWidth="lg">
                    <span className={classes.overline}>What LIA can help with</span>
                    <h2 id="lia-helps" className={classes.heading}>Ask a question, find your way</h2>
                    <p className={classes.lead}>
                        LIA knows the Ministry of Finance website: its publications, services, tenders and news.
                        Ask in your own words and it points you to the right page or document.
                    </p>
                    <Grid container spacing={3}>
                        {HELPS.map((item) => (
                            <Grid item xs={12} sm={6} md={3} key={item.title}>
                                <div className={classes.helpCard}>
                                    <span className={classes.helpIcon}>{item.icon}</span>
                                    <h3 className={classes.helpTitle}>{item.title}</h3>
                                    <p className={classes.helpText}>{item.text}</p>
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </section>

            <section id="lia-chat" className={`${classes.section} ${classes.muted}`} aria-labelledby="lia-chat-title">
                <Container maxWidth="lg">
                    <span className={classes.overline}>Talk to LIA</span>
                    <h2 id="lia-chat-title" className={classes.heading}>Start a conversation</h2>
                    <p className={classes.lead}>Choose your language at the top of the chat, then type your question or pick a suggestion.</p>
                    <ChatBot embedded />
                </Container>
            </section>

            <section className={classes.section} aria-labelledby="lia-about">
                <Container maxWidth="lg">
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <div className={classes.aboutCard}>
                                <h2 id="lia-about" className={classes.aboutTitle}>Why “LIA”?</h2>
                                <p className={classes.aboutText}>
                                    In Tetum, <strong lang="tet">lia</strong> means word, speech and information: what an
                                    assistant gives you. The name also stands for <strong>{LIA.expansion}</strong>.
                                </p>
                                <p className={classes.aboutText}>
                                    LIA’s mascot is a speech bubble wearing a gold <strong lang="tet">kaibauk</strong>, the
                                    traditional crescent headdress, and a sash woven with <strong lang="tet">tais</strong> motifs:
                                    a digital helper dressed in Timor-Leste’s heritage.
                                </p>
                            </div>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <div className={classes.aboutCard}>
                                <h2 className={classes.aboutTitle}>Using LIA responsibly</h2>
                                <ul className={classes.useList}>
                                    {RESPONSIBLE_USE.map((text) => (
                                        <li key={text} className={classes.useItem}>
                                            <CheckCircleOutlineIcon className={classes.useIcon} fontSize="small" />
                                            <span>{text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Grid>
                    </Grid>
                </Container>
            </section>
        </>
    );
}
